import { useEffect, useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { getCareers } from '../lib/api';
import type { CareerJobPosting } from '../lib/types';
import '../styles/Careers.css';
import ShareableHeading from './ShareableHeading';
import CareerApplicationForm from './CareerApplicationForm';

const WHY_JOIN_KEYS = [
  'careers.whyJoin.item1',
  'careers.whyJoin.item2',
  'careers.whyJoin.item3',
  'careers.whyJoin.item4',
  'careers.whyJoin.item5',
] as const;

const WHY_JOIN_FALLBACKS: Record<string, Record<string, string>> = {
  hi: {
    'careers.whyJoin.item1': '✨ शिक्षा पर वास्तविक प्रभाव डालें',
    'careers.whyJoin.item2': '🌍 अनोखे सांस्कृतिक परिवेश में कार्य करें',
    'careers.whyJoin.item3': '📚 संस्कृत और दर्शन विशेषज्ञों से सीखें',
    'careers.whyJoin.item4': '🤝 अर्थपूर्ण साझेदारियाँ बनाएं',
    'careers.whyJoin.item5': '💼 अपना व्यावसायिक नेटवर्क बढ़ाएँ',
  },
  en: {
    'careers.whyJoin.item1': '✨ Make a real impact on education',
    'careers.whyJoin.item2': '🌍 Work in unique cultural settings',
    'careers.whyJoin.item3': '📚 Learn from Sanskrit & philosophy experts',
    'careers.whyJoin.item4': '🤝 Build meaningful partnerships',
    'careers.whyJoin.item5': '💼 Grow your professional network',
  },
  sa: {
    'careers.whyJoin.item1': '✨ शिक्षायां वास्तविकं प्रभावं कुरुत',
    'careers.whyJoin.item2': '🌍 अद्वितीयेषु सांस्कृतिकपरिवेशेषु कार्यं कुरुत',
    'careers.whyJoin.item3': '📚 संस्कृत-दर्शनविशेषज्ञेभ्यः शिक्षध्वम्',
    'careers.whyJoin.item4': '🤝 अर्थपूर्णाः साझेदारीः निर्मायत',
    'careers.whyJoin.item5': '💼 स्वव्यावसायिकजालं वर्धयत',
  },
};

const OFFICE_BODY_FALLBACKS: Record<string, string> = {
  hi: 'हमारा धर्मशाला कार्यालय भारत के सबसे जीवंत सांस्कृतिक केन्द्रों में से एक में स्थित है। तिब्बती संस्कृति, शिक्षा, और पर्यटन के केन्द्र के रूप में धर्मशाला प्राकृतिक सौन्दर्य, आध्यात्मिक अध्ययन, और व्यावसायिक अवसरों का अनूठा संगम प्रस्तुत करता है।',
  en: "Our Dharamshala office is nestled in one of India's most vibrant cultural hubs. Known as a center for Tibetan culture, education, and tourism, Dharamshala offers a unique blend of natural beauty, spiritual learning, and professional opportunities.",
  sa: 'अस्माकं धर्मशाला-कार्यालयः भारतस्य अतीव जीवितेषु सांस्कृतिककेन्द्रेषु अन्यतमः। तिब्बती-संस्कृतेः, शिक्षायाः, पर्यटनस्य च केन्द्रत्वेन धर्मशाला प्राकृतिकसौन्दर्यं, आध्यात्मिकशिक्षां, व्यावसायिकावकाशान् च अनोखे संयोगे प्रयच्छति।',
};

function safeLocalized(t: ReturnType<typeof useGlossary>['t'], key: string, fallback: string): string {
  const value = t(key);
  if (value !== key) return value;
  return fallback || key;
}

export default function Careers() {
  const { t, locale } = useGlossary();
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [applicationJob, setApplicationJob] = useState<string | null>(null);
  const [jobs, setJobs] = useState<CareerJobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const toggleJobDetails = (jobId: string) => {
    const nextExpandedJob = expandedJob === jobId ? null : jobId;
    setExpandedJob(nextExpandedJob);
    if (applicationJob && applicationJob !== nextExpandedJob) {
      setApplicationJob(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCareers(locale)
      .then((data) => {
        if (cancelled) return;
        setJobs(data.jobs ?? []);
        setExpandedJob(null);
        setApplicationJob(null);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? t('careers.error.load') : t('careers.noJobs'));
        setJobs([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale, t]);

  return (
    <div className="careers">
      <header className="careers-header">
        <h1>{t('careers.title')}</h1>
        <p className="subtitle">{t('careers.subtitle')}</p>
        <p className="description">{t('careers.description')}</p>
      </header>

      <section className="careers-content">
        <div className="job-listings">
          <ShareableHeading id="open-positions" as="h2" className="section-title">{t('careers.openPositions')}</ShareableHeading>

           {loading ? (
             <div className="no-jobs">
               <p>{t('careers.loading')}</p>
             </div>
           ) : jobs.length === 0 ? (
             <div className="no-jobs">
               <p>{error ?? t('careers.noJobs')}</p>
             </div>
           ) : (
             <div className="jobs-grid">
                {jobs.map((job) => (
                  <div key={job.id} id={`job-${job.id}`} className="job-card">
                     <div className="job-header" onClick={() => toggleJobDetails(job.id)}>
                      <div className="job-title-section">
                      <ShareableHeading id={`job-${job.id}`} as="h3" className="job-title">{job.title}</ShareableHeading>
                      <p className="job-location">📍 {job.location}</p>
                    </div>
                    <div className="job-meta">
                      <span className="job-type">{job.type}</span>
                      <span className="expand-icon">{expandedJob === job.id ? '▼' : '▶'}</span>
                    </div>
                  </div>

                  <p className="job-excerpt">{job.description.substring(0, 150)}...</p>

                  {expandedJob === job.id && (
                    <div className="job-details">
                      <div className="detail-section">
                        <h4>{t('careers.role.about')}</h4>
                        <p>{job.description}</p>
                      </div>

                      <div className="detail-section">
                        <h4>{t('careers.role.responsibilities')}</h4>
                        <ul className="responsibilities-list">
                          {job.responsibilities.map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="detail-section">
                        <h4>{t('careers.role.qualifications')}</h4>
                        <ul className="qualifications-list">
                          {job.qualifications.map((qual, idx) => (
                            <li key={idx}>{qual}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="detail-section two-column">
                        <div className="column">
                          <h4>{t('careers.role.skills')}</h4>
                          <ul className="skills-list">
                            {job.skills.map((skill, idx) => (
                              <li key={idx}>{skill}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="column">
                          <h4>{t('careers.role.benefits')}</h4>
                          <ul className="benefits-list">
                            {job.benefits.map((benefit, idx) => (
                              <li key={idx}>{benefit}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                       <div className="application-section">
                         <p className="application-note">
                           <strong>{t('careers.contract.title')}</strong> {t('careers.contract.body')}
                         </p>
                         <button
                           type="button"
                           className="apply-button"
                           onClick={(event) => {
                             event.stopPropagation();
                             setApplicationJob((current) => (current === job.id ? null : job.id));
                           }}
                         >
                           {applicationJob === job.id ? '✕' : t('careers.apply')}
                         </button>
                         {applicationJob === job.id && (
                           <CareerApplicationForm
                             job={job}
                             onCancel={() => setApplicationJob(null)}
                           />
                         )}
                       </div>
                     </div>
                   )}
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="careers-sidebar">
          <div className="sidebar-card">
            <ShareableHeading id="why-join" as="h3">{t('careers.whyJoin')}</ShareableHeading>
            <ul>
              {WHY_JOIN_KEYS.map((key) => (
                <li key={key}>{safeLocalized(t, key, WHY_JOIN_FALLBACKS[locale]?.[key] ?? WHY_JOIN_FALLBACKS.en[key])}</li>
              ))}
            </ul>
          </div>

          <div className="sidebar-card">
            <ShareableHeading id="dharamshala-office" as="h3">{t('careers.office')}</ShareableHeading>
            <p>{safeLocalized(t, 'careers.office.body', OFFICE_BODY_FALLBACKS[locale] ?? OFFICE_BODY_FALLBACKS.en)}</p>
          </div>

          <div className="sidebar-card apply-card">
            <h3>{t('careers.questions')}</h3>
            <p>{t('careers.contactHr')}</p>
            <p className="contact-email">
              <strong>{t('careers.email')}</strong> <a href="mailto:career@raajadharma.org">career@raajadharma.org</a>
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
